import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn, PageHeader } from '@payment-plan/ui';
import { CatalogUnits } from './CatalogUnits';
import { CatalogUpload } from './CatalogUpload';
import { textoSegunAcceso, useAccesoAdmin } from '../acceso-admin';
import { InmobiliariaSelector } from '../inmobiliaria-activa/inmobiliaria-activa';

type Vista = 'unidades' | 'carga';

const VISTAS: Array<{ id: Vista; etiqueta: string }> = [
  { id: 'unidades', etiqueta: 'Unidades' },
  { id: 'carga', etiqueta: 'Cargar Excel' },
];

/**
 * change-70 · Catálogo con dos vistas: «Unidades» (consulta, `catalogo.ver`) para todos y «Cargar
 * Excel» solo con `catalogo.cargar`. La vista viaja en `?vista=` (enlace directo), pero sin el permiso
 * la carga no existe aunque se fuerce la URL: el ADMIN_INMOBILIARIA queda en solo lectura.
 */
export function CatalogPage(): React.JSX.Element {
  const acceso = useAccesoAdmin('catalogo');
  const [params, setParams] = useSearchParams();
  const vista: Vista = acceso.cargar && params.get('vista') === 'carga' ? 'carga' : 'unidades';

  const elegir = (id: Vista): void => {
    const siguiente = new URLSearchParams(params);
    if (id === 'carga') siguiente.set('vista', 'carga');
    else siguiente.delete('vista');
    setParams(siguiente, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        eyebrow="Administración"
        title="Catálogo de unidades"
        description={textoSegunAcceso(acceso, {
          gestion: 'Consulta las unidades de cada proyecto, ajusta precios y carga el catálogo desde Excel.',
          consulta: 'Consulta las unidades de los proyectos de tu inmobiliaria, con su precio y estado.',
        })}
      />

      <InmobiliariaSelector />

      {acceso.cargar && (
        <div
          role="tablist"
          aria-label="Vista del catálogo"
          className="inline-flex rounded-lg border border-border/70 bg-surface-fade p-1"
        >
          {VISTAS.map((v) => {
            const activa = v.id === vista;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                id={`catalogo-tab-${v.id}`}
                aria-selected={activa}
                aria-controls={`catalogo-panel-${v.id}`}
                tabIndex={activa ? 0 : -1}
                onClick={() => elegir(v.id)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    elegir(v.id === 'unidades' ? 'carga' : 'unidades');
                  }
                }}
                className={cn(
                  'h-10 rounded-md px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  activa ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v.etiqueta}
              </button>
            );
          })}
        </div>
      )}

      <section
        id={`catalogo-panel-${vista}`}
        role={acceso.cargar ? 'tabpanel' : undefined}
        aria-labelledby={acceso.cargar ? `catalogo-tab-${vista}` : undefined}
      >
        {vista === 'carga' ? <CatalogUpload /> : <CatalogUnits acceso={acceso} />}
      </section>
    </div>
  );
}
